/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { Plan, Plans } from 'cio/utils/billing/plans';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import MasqService from 'cio/services/masq';
import RouterService from '@ember/routing/router-service';
import AccountService from 'cio/services/account';
import AnalyticsService from 'cio/services/analytics';
import BillingService from 'cio/services/billing';
import DS from 'ember-data';
import { PlanType } from 'cio/models/plan';
import getCurrentPlan from 'cio/utils/billing/current-plan';
import { action } from '@ember/object';

interface PlanInfoArgs {
  planTemplate: Plan;
  nextUpgradePlanType: PlanType;
  cdpPremiumPrice?: number;
  showCheckout: boolean;
  hideExpirationLabel?: boolean;
  onStartCheckout: () => void;
  onShowDowngradeModal: (value: boolean) => unknown;
}

export default class PlanInfo extends Component<PlanInfoArgs> {
  @service masq!: MasqService;
  @service billing!: BillingService;
  @service analytics!: AnalyticsService;
  @service account!: AccountService;
  @service store!: DS.Store;
  @service router!: RouterService;

  @tracked trialExpired = this.billing.daysLeftInTrial <= 0;
  @tracked hasPinnedPlan = this.billing.pinnedPlan !== undefined;
  @tracked pinnedPlan = this.billing.pinnedPlan;
  @tracked isPremiumTrial = this.billing.billingInfo?.premiumTrial ?? false;
  @tracked showLabelForEssentials = this.args.planTemplate.name === Plans.Essentials.name && !this.isPremiumTrial;
  @tracked showLabelForPremium = this.args.planTemplate.planType === Plans.Premium.planType && this.isPremiumTrial;

  @computed('billing.subscription.active')
  get hasActiveSubscription() {
    return this.billing.subscription?.active === true;
  }

  @computed('billing.subscription.plan.planType')
  get subscribedPlan() {
    return this.billing.subscription?.plan?.planType;
  }

  get hideExpirationLabel() {
    return this.hasActiveSubscription || this.args.showCheckout || this.args.hideExpirationLabel;
  }

  get expirationLabelText() {
    if (this.hasActiveSubscription === true) {
      return;
    } else if (this.trialExpired) {
      return 'Your trial has expired';
    } else {
      return this.billing.planSummary;
    }
  }

  get cdpPremiumPrice() {
    return this.args.cdpPremiumPrice ?? 0;
  }

  get isPrimary() {
    return this.args.planTemplate.planType === this.args.nextUpgradePlanType;
  }

  get essentialsCheckoutRoute() {
    if (this.router.currentRouteName === 'settings.subscription.index') {
      return 'settings.subscription.checkout';
    }
    return 'settings.billing.checkout';
  }

  // CDP only has two plan options, these cards are wider than journey plans.
  // We need to set the width specifically for the CDP cards so the separator spans the whole card.
  get isCdpPlan() {
    return (
      this.args.planTemplate.planType === Plans.CdpPremium.planType ||
      this.args.planTemplate.planType === Plans.CdpStarter.planType
    );
  }

  @action
  trackCtaButtonClick(text: string) {
    let { account, billing } = this;
    let currentPlan = getCurrentPlan(billing.billingInfo, billing.subscription?.plan.planType);
    this.analytics.track('plan_billing', 'buy_or_request_access_clicked', {
      data: {
        account_id: account.current.id,
        current_plan: currentPlan,
        requested_plan: this.args.planTemplate.planType,
        text,
      },
    });
  }
}
